import { inject } from '@angular/core';
import { CanActivateFn, Router, Routes } from '@angular/router';
import { environment } from '@environment';
import { ShellComponent } from './shell/shell.component';
import { NotificationSettingsPageComponent, NotificationsPageComponent } from '@lite-workspace/shared/blocks/notifications';
import { onboardingGuard } from './core/onboarding.guard';
import { studentEditorAccessGuard } from './core/student-editor-access.guard';
import { editorPrefetchGuard } from './core/editor-prefetch.guard';
import { clientCabinetGuard } from './core/client-cabinet.guard';
import { clientAlbumHomeGuard, clientAlbumOffGuard } from './core/client-album-home.guard';
import { clientPinEnrollmentGuard } from './core/client-pin-enrollment.guard';
import { designApprovedGuard } from './core/design-approved.guard';
import { superAdminVerdictGuard } from './core/project-role';
import { designerModeGuard } from './core/designer-mode.guard';
import { notPartnerGuard, partnerCabinetGuard, partnerRootRedirect } from './core/partner-cabinet.guard';
import { projectWizardLeaveGuard } from './features/projects/feature/project-wizard-leave.guard';
import { projectWizardEntryGuard } from './features/projects/feature/project-wizard-entry.guard';
import { demoProjectReadyGuard } from './features/projects/feature/demo-project-ready.guard';
import { retouchRouteGuard } from './features/projects/feature/retouch-route.guard';
import { retouchEditorGuard } from './core/retouch-editor.guard';
import {
  organizerCabinetRedirectGuard,
  organizerHomeGuard,
  organizerSectionRedirectGuard,
  organizerSelectWizardGuard,
  projectSettingsRedirectGuard,
} from './features/projects/feature/organizer-home.guard';
import {
  labEntryMatcher,
  labEntryResolve,
  ownCatalogGuard,
  partnerMarkGuard,
  pendingLabEntryGuard,
} from '@lite-workspace/shared/lab-entry';

// «Маркетинг» lives in Studio Manager; the old address hands over in this tab. No CRM on this build = no section.
const studioManagerMarketingGuard: CanActivateFn = () => {
  const crm = environment.STUDIO_MANAGER_APP_URL;
  const router = inject(Router);
  if (!crm) return router.parseUrl('/projects');
  // First navigation = a typed/bookmarked address: keeping it in history would bounce Back from CRM here again.
  window.location[router.navigated ? 'assign' : 'replace'](`${crm}/marketing`);
  return false;
};

export const appRoutes: Routes = [
  {
    path: '',
    component: ShellComponent,
    canActivate: [clientCabinetGuard, onboardingGuard],
    // Guards of one route run at once: as a child guard the lab replay waits for the session check instead of
    // eating a guest's pending record before the login.
    canActivateChild: [pendingLabEntryGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: partnerRootRedirect },
      {
        // Маршруты кабинета без `:projectId` — фотографские: организатора с них возвращает на его проект.
        path: 'projects',
        canActivate: [organizerCabinetRedirectGuard],
        loadComponent: () => import('./features/projects/feature/projects-page.component').then(m => m.ProjectsPageComponent),
      },
      {
        // Standalone «Создать проект» wizard — inside the cabinet shell, entered from the
        // create-project modal. Registered above projects/:projectId so "new" never matches as an id.
        path: 'projects/new',
        canActivate: [organizerCabinetRedirectGuard, projectWizardEntryGuard],
        // Уход при живой загрузке (кнопка «назад», ссылка в кабинет) спрашивает то же, что крестик мастера.
        canDeactivate: [projectWizardLeaveGuard],
        loadComponent: () =>
          import('./features/projects/feature/project-wizard-page.component').then(m => m.ProjectWizardPageComponent),
      },
      {
        // The parent shell's canActivate does not re-run on in-app sibling navigation (list → project),
        // so the onboarding gate is re-asserted here: every project entry — direct URL or click — is
        // re-checked against THAT project's album, matching the editor/preview routes below.
        path: 'projects/:projectId',
        canActivate: [clientCabinetGuard, onboardingGuard, demoProjectReadyGuard],
        loadComponent: () => import('./features/projects/feature/project-shell.component').then(m => m.ProjectShellComponent),
        children: [
          {
            // Корень проекта у организатора — его собственный экран «Ученики»; у фотографа гвард не
            // совпадает и маршрут падает в сегодняшний редирект ниже.
            path: '',
            pathMatch: 'full',
            canMatch: [organizerHomeGuard],
            loadComponent: () =>
              import('./features/projects/feature/organizer-students-page.component').then(m => m.OrganizerStudentsPageComponent),
          },
          { path: '', pathMatch: 'full', redirectTo: 'albums' },
          {
            path: 'albums',
            canActivate: [organizerSectionRedirectGuard],
            data: { veil: 'rows' },
            loadComponent: () => import('./features/projects/feature/albums-page.component').then(m => m.AlbumsPageComponent),
          },
          {
            path: 'photos',
            canActivate: [organizerSectionRedirectGuard],
            data: { veil: 'tiles' },
            loadComponent: () => import('./features/projects/feature/photos-page.component').then(m => m.PhotosPageComponent),
          },
          {
            path: 'retouch',
            canActivate: [organizerSectionRedirectGuard, retouchRouteGuard],
            data: { veil: 'tiles' },
            loadComponent: () => import('./features/projects/feature/retouch-page.component').then(m => m.RetouchPageComponent),
          },
          {
            path: 'faces',
            canActivate: [organizerSectionRedirectGuard],
            data: { veil: 'faces' },
            loadComponent: () => import('./features/projects/feature/faces-page.component').then(m => m.FacesPageComponent),
          },
          {
            // Экран кадров одной персоны. Живёт под faces/, чтобы таб «Лица» оставался активным.
            path: 'faces/:personId',
            canActivate: [organizerSectionRedirectGuard],
            loadComponent: () =>
              import('./features/projects/feature/person-faces-page.component').then(m => m.PersonFacesPageComponent),
          },
          {
            path: 'purchases',
            canActivate: [organizerSectionRedirectGuard],
            data: { veil: 'rows' },
            loadComponent: () =>
              import('./features/upsale/feature/project-purchases-page.component').then(m => m.ProjectPurchasesPageComponent),
          },
          {
            path: 'messages',
            canActivate: [organizerSectionRedirectGuard],
            data: { veil: 'rows' },
            loadComponent: () =>
              import('./features/projects/feature/project-messages-page.component').then(m => m.ProjectMessagesPageComponent),
          },
          {
            // Настройки проекта живут в редакторе мастера — старый адрес только перенаправляет.
            path: 'settings',
            canActivate: [organizerSectionRedirectGuard, projectSettingsRedirectGuard],
            children: [],
          },
        ],
      },
      {
        path: 'designs',
        canActivate: [organizerCabinetRedirectGuard],
        loadComponent: () => import('./features/designs/designs-page.component').then(m => m.DesignsPageComponent),
      },
      {
        // Standalone «Добавить дизайн» wizard — lives INSIDE the cabinet shell (keeps the thin cabinet
        // topbar). Its own in-card app-bar/menu is suppressed (a separate surface owns that).
        path: 'designs/new',
        canActivate: [organizerCabinetRedirectGuard],
        loadComponent: () => import('./features/designs/design-wizard-page.component').then(m => m.DesignWizardPageComponent),
      },
      {
        path: 'typographies',
        canMatch: [ownCatalogGuard],
        canActivate: [organizerCabinetRedirectGuard],
        loadChildren: () => import('./features/printers/own-print-product-host.provider').then(m => m.TYPOGRAPHIES_ROUTES),
      },
      { path: 'accounting', redirectTo: 'projects' },
      {
        // Guards of one array run at once: the CRM hand-over sits on the child so the organizer redirect settles first.
        path: 'marketing',
        canActivate: [organizerCabinetRedirectGuard],
        children: [{ path: '', canActivate: [studioManagerMarketingGuard], children: [] }],
      },
      {
        path: 'account',
        canActivate: [organizerCabinetRedirectGuard],
        loadComponent: () => import('./features/account/feature/account-page.component').then(m => m.AccountPageComponent),
      },
      // The shared bell links here. Eager: the shell already imports this lib statically for the bell.
      { path: 'notifications', canActivate: [organizerCabinetRedirectGuard], component: NotificationsPageComponent },
      {
        path: 'notifications/settings',
        canActivate: [organizerCabinetRedirectGuard],
        component: NotificationSettingsPageComponent,
      },
      {
        path: 'partner',
        canActivate: [partnerCabinetGuard],
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'sales' },
          {
            path: 'sales',
            loadComponent: () => import('./features/partner/sales-page.component').then(m => m.PartnerSalesPageComponent),
          },
          {
            path: 'templates',
            loadComponent: () => import('./features/partner/templates-page.component').then(m => m.PartnerTemplatesPageComponent),
          },
          {
            // The partner's «Новый шаблон» — the SAME wizard as the photographer's designs/new, not a fork.
            // The partner mode travels through route data (withComponentInputBinding binds data keys to the
            // component's input() by name): it retargets the exit and switches the step-3 copy.
            path: 'templates/new',
            data: { partner: true },
            loadComponent: () => import('./features/designs/design-wizard-page.component').then(m => m.DesignWizardPageComponent),
          },
        ],
      },
    ],
  },
  {
    // In-cabinet selection wizard: the photographer runs the student's selection under their own
    // session — the session slot is one per browser, so opening the student link instead would take
    // over the photographer's auth state (the client entry pages warn via PrincipalSwitchService
    // before doing that). Full-screen like the client wizard, hence outside the project shell.
    path: 'projects/:projectId/select/:personId',
    canActivate: [clientCabinetGuard, onboardingGuard, organizerSelectWizardGuard],
    loadComponent: () =>
      import('./features/client/feature/client-selection-page.component').then(m => m.ClientSelectionPageComponent),
  },
  {
    // Personal token link (links mode) — MUST precede client/:linkId or the 't' segment is eaten as a linkId.
    path: 'client/t/:token',
    loadComponent: () =>
      import('./features/client/feature/client-token-entry-page.component').then(m => m.ClientTokenEntryPageComponent),
  },
  {
    // The student link from the share dialog — pick yourself, log in as a person, land in the client zone.
    path: 'client/:linkId',
    loadComponent: () => import('./features/client/feature/client-entry-page.component').then(m => m.ClientEntryPageComponent),
  },
  {
    path: 'client/:linkId/contact',
    loadComponent: () =>
      import('./features/client/feature/client-contact-card-page.component').then(m => m.ClientContactCardPageComponent),
  },
  {
    path: 'client/:linkId/gallery',
    canActivate: [clientPinEnrollmentGuard, clientAlbumHomeGuard],
    loadComponent: () =>
      import('./features/client/feature/client-gallery-page.component').then(m => m.ClientGalleryPageComponent),
  },
  {
    path: 'client/:linkId/album',
    canActivate: [clientPinEnrollmentGuard, clientAlbumHomeGuard, clientAlbumOffGuard],
    loadComponent: () => import('./features/client/feature/client-album-page.component').then(m => m.ClientAlbumPageComponent),
  },
  {
    path: 'client/:linkId/select',
    canActivate: [clientPinEnrollmentGuard, clientAlbumHomeGuard],
    loadComponent: () =>
      import('./features/client/feature/client-selection-page.component').then(m => m.ClientSelectionPageComponent),
  },
  {
    path: 'client/:linkId/assembling',
    canActivate: [clientPinEnrollmentGuard, clientAlbumOffGuard],
    loadComponent: () =>
      import('./features/client/feature/client-assembling-page.component').then(m => m.ClientAssemblingPageComponent),
  },
  {
    path: 'auth/callback',
    loadComponent: () => import('./features/auth/auth-callback-lazy').then(m => m.AuthCallbackComponent),
  },
  // A print lab's entry link `/<slug>` → `lab/<key>`; every other head is reserved in LAB_ENTRY_RESERVED_HEADS.
  { matcher: labEntryMatcher, canMatch: [labEntryResolve('lab')], children: [] },
  {
    // The mark is a child guard: a guest goes to the login first, the mark runs only with a session.
    path: 'lab/:key',
    canActivate: [clientCabinetGuard],
    children: [{ path: '', canActivate: [partnerMarkGuard], children: [] }],
  },
  // Диагностика локального SSO-тупика (FKS-12060) — инструмент разработчика, не продуктовый экран:
  // в продовой сборке роута нет вовсе, адрес там падает в общий 404. Чанк ленивый, в главный бандл
  // компонент не попадает.
  ...(environment.production
    ? []
    : [
        {
          path: 'auth/no-local-session',
          loadComponent: () => import('./features/auth/no-local-session.component').then(m => m.NoLocalSessionComponent),
        },
      ]),
  {
    // The organizer link from the share dialog — logs in by coordinator code and lands on the project
    // root, which for them is the students screen.
    path: 'auth/projects/:projectId/coordinator/:coordinatorCode',
    loadComponent: () => import('./features/auth/coordinator-login.component').then(m => m.CoordinatorLoginComponent),
  },
  {
    // Онбординг — вход в кабинет фотографа (в нём же живёт удаление кадров у персоны): закрыт тем же
    // гвардом, что и остальной кабинет, и тем же правилом «кабинет без :projectId — не организатора».
    path: 'onboarding',
    canActivate: [clientCabinetGuard, organizerCabinetRedirectGuard],
    loadComponent: () => import('./features/onboarding/onboarding-page.component').then(m => m.OnboardingPageComponent),
  },
  {
    // Возвратный отказ гварда кабинета. Без гварда намеренно: иначе отказ замкнулся бы сам на себя.
    path: 'cabinet-access-denied',
    loadComponent: () => import('./features/auth/cabinet-access-denied.component').then(m => m.CabinetAccessDeniedComponent),
  },
  {
    path: 'designs/tunnel',
    canActivate: [clientCabinetGuard, organizerCabinetRedirectGuard],
    loadComponent: () => import('./features/designs/tunnel/tunnel-step1.component').then(m => m.TunnelStep1Component),
  },
  {
    path: 'design-editor/:designPackageId',
    canMatch: [designerModeGuard],
    canActivate: [
      clientCabinetGuard,
      organizerCabinetRedirectGuard,
      notPartnerGuard,
      onboardingGuard,
      designApprovedGuard,
      superAdminVerdictGuard,
    ],
    loadComponent: () => import('./features/designs/design-editor-entry.component').then(m => m.DesignEditorEntryComponent),
  },
  {
    path: 'editor/:projectId/:albumId',
    canActivate: [editorPrefetchGuard, onboardingGuard, studentEditorAccessGuard],
    // The root veil draws the editor's own shutter while the guards and the role resolve: one loader from the click.
    data: { bootShutter: 'album' },
    loadChildren: () => import('./core/album-editor.routes').then(m => m.albumEditorRoutes),
  },
  {
    // Full-screen auto-retouch of the selection `?files=<id>,<id>`; outside the project shell like the album editor.
    path: 'retouch-editor/:projectId',
    canActivate: [clientCabinetGuard, onboardingGuard, retouchEditorGuard],
    loadChildren: () => import('./core/retouch-editor.routes').then(m => m.retouchEditorRoutes),
  },
  {
    path: 'preview/:projectId/:albumId',
    canActivate: [editorPrefetchGuard, onboardingGuard],
    data: { bootShutter: 'album' },
    loadChildren: () => import('./core/album-editor.routes').then(m => m.albumEditorRoutes),
  },
  {
    path: '**',
    // Home address and wordmark travel through route data (withComponentInputBinding binds data keys
    // to the component's inputs) — the page itself is shared with the Фотокниги cabinet.
    loadComponent: () => import('@lite-workspace/shared/helm-ui/not-found').then(m => m.NotFoundPageComponent),
    // Перечислены ВСЕ входы компонента: withComponentInputBinding пишет каждый из data,
    // и пропущенный ключ пришёл бы как undefined, затерев дефолт.
    data: {
      homeLink: '/projects',
      logoWord: 'Альбомы',
      homeLabel: 'К проектам',
      clientHint: 'Попроси у фотографа новую.',
      fill: false,
    },
  },
];
